import { graphMutation, graphQuery } from '@services/commService';
import { CreateTimeTrackingManuallyInput, RetroactiveTimeTracking, UpdateTimeTrackingManuallyInput } from './types';

const getTimeTrackingsQuery = `
	query GetTimeTrackings($userId: String!, $mentorId: String!, $from: String!, $to: String!) {
		timeTrackings(userId: $userId, mentorId: $mentorId, from: $from, to: $to) {
			id
			userId
			mentorId
			trackingType
			activityType
			activityName
			startTime
			duration
			endTime
			comments
			createdAt
			updatedAt
			retroactiveStartTime
			retroactiveDuration
			retroactiveEndTime
		}
	}
`;

const createTimeTrackingMutation = `
	mutation CreateTimeTracking(
		$userId: String!
		$mentorId: String!
		$trackingType: TrackingTypeEnum!
		$activityType: ActivityTypeEnum!
		$comments: String
	    $retroactiveStartTime: String!
        $retroactiveDuration: Int!
        $retroactiveEndTime: String!
	) {
		createTimeTracking(
			userId: $userId
			mentorId: $mentorId
			trackingType: $trackingType
			activityType: $activityType
			comments: $comments
			retroactiveStartTime: $retroactiveStartTime
			retroactiveDuration: $retroactiveDuration
			retroactiveEndTime: $retroactiveEndTime
		) {
			id
			userId
			mentorId
			trackingType
			activityType
			startTime
			duration
			endTime
			comments
			createdAt
			updatedAt
			retroactiveStartTime
			retroactiveDuration
			retroactiveEndTime
		}
	}
`;

const updateTimeTrackingMutation = `
	mutation UpdateTimeTracking(
		$id: String!
		$userId: String!
		$mentorId: String!
		$trackingType: TrackingTypeEnum!
		$activityType: ActivityTypeEnum!
		$comments: String
		$retroactiveStartTime: String
        $retroactiveDuration: Int
        $retroactiveEndTime: String
	) {
		updateTimeTracking(
			id: $id
			userId: $userId
			mentorId: $mentorId
			trackingType: $trackingType
			activityType: $activityType
			comments: $comments
			retroactiveStartTime: $retroactiveStartTime
			retroactiveDuration: $retroactiveDuration
			retroactiveEndTime: $retroactiveEndTime
		) {
			id
			userId
			mentorId
			trackingType
			activityType
			comments
			updatedAt
			retroactiveStartTime
			retroactiveDuration
			retroactiveEndTime
		}
	}
`;

const deleteTimeTrackingMutation = `
	mutation DeleteTimeTracking($id: String!) {
		deleteTimeTracking(id: $id) {
			id
			userId
			mentorId
			activityType
			startTime
			duration
			endTime
			comments
			createdAt
			updatedAt
		}
	}
`;

export const getTimeTrackings = async (
  userId: string,
  mentorId: string,
  from: string,
  to: string,
): Promise<RetroactiveTimeTracking[]> => {
  const response = await graphQuery(getTimeTrackingsQuery, true, { userId, mentorId, from, to });
  const records = response?.data?.timeTrackings;
  if (!records) {
    throw new Error('Failed to fetch time tracking records');
  }
  return records;
};

export const createTimeTrackingManually = async (
  input: CreateTimeTrackingManuallyInput,
): Promise<RetroactiveTimeTracking> => {
  const response = await graphMutation(createTimeTrackingMutation, input, true);
  const newRecord = response?.data?.createTimeTracking;
  if (!newRecord) {
    throw new Error('Failed to create time tracking record');
  }
  return newRecord;
};

export const updateTimeTrackingManually = async (
  input: UpdateTimeTrackingManuallyInput,
): Promise<RetroactiveTimeTracking> => {
  const response = await graphMutation(updateTimeTrackingMutation, input, true);
  const updatedRecord = response?.data?.updateTimeTracking;
  if (!updatedRecord) {
    throw new Error('Failed to update time tracking record');
  }
  return updatedRecord;
};

export const deleteTimeTrackingManually = async (id: string): Promise<RetroactiveTimeTracking> => {
  const response = await graphMutation(deleteTimeTrackingMutation, { id }, true);
  const deletedRecord = response?.data?.deleteTimeTracking;
  if (!deletedRecord) {
    throw new Error('Failed to delete time tracking record');
  }
  return deletedRecord;
};
